import {
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent as ReactChangeEvent,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { LIMITS, expandShortcodes, snippetOf, type Member } from '@engineer-os/chat';
import { useStrings } from '../../state/useLocale';
import { Button, Parts } from '../../ui';
import type { ChatRow } from '../../state/useChat';
import { RichText } from './CustomEmoji';
import { EmojiPicker } from './EmojiPicker';
import { Identity } from './Identity';
import { mentionCandidates, mentionTags, typedMention } from './mentions';
import { imageFrom } from '../../lib/image';
import type { AttachmentApi } from '../../state/useAttachment';

/**
 * Where a message is typed.
 *
 * Enter sends and Shift+Enter breaks the line, the convention of every chat
 * a learner has used; a form submit does the same for the pointer. The box
 * grows with the text up to four lines and scrolls after that, so a long
 * thought never pushes the conversation off the top of the panel.
 *
 * The count appears only past 80% of the limit. Below that it is noise;
 * above it, it is the one thing the writer needs to know. The limit itself
 * is the protocol's, so the server's refusal and this counter cannot drift.
 *
 * The draft is the parent's state, not this component's, so it survives
 * the panel closing. The textarea is never disabled: you can write while
 * the line is down and send when it is back. Only the button waits.
 *
 * Two ways to reach an emoji, and they are the same two everybody already
 * has. Typing `:rocket:` turns into the character as the closing colon
 * lands, and so do half a dozen emoticons; the palette is for when you do
 * not know what a thing is called. Both end in a character in the text,
 * because the protocol has no idea any of this happened — a message is a
 * string, and a tab that never opened the palette renders exactly what was
 * sent.
 *
 * ## Answering one person
 *
 * Two things were added, and both are about the same problem: a room where
 * nobody can address anybody is a room where everybody is talking at once.
 *
 * **A reply** puts a bar above the field naming who is being answered, with a
 * way out of it. The bar is here rather than in the list because this is where
 * the writing happens, and a reply you have forgotten you are in is a reply
 * sent to the wrong person. It is the parent's state, not this component's, so
 * that pressing Reply on a message is one decision and the panel owns it.
 *
 * **A mention** is typed as `@` and a name, and the name is only ever what is
 * typed. What travels is the tag — four hex characters the server derived from
 * an id nobody else has — because names collide and a name is a string one
 * person can type about another. The tags are worked out from the finished
 * text at the moment Send is pressed (mentions.ts), not remembered from what
 * the list inserted, so deleting half a name deletes the mention and typing a
 * name by hand makes one. What the reader sees highlighted is, by
 * construction, exactly what was addressed.
 *
 * The suggestion list is a listbox the field points at rather than a thing
 * that takes focus: the caret has to stay in the sentence being written, so
 * the arrow keys move a highlight the field reports through
 * `aria-activedescendant`, Enter and Tab take the highlighted name, and
 * Escape puts the list away without putting the sentence away with it.
 *
 * ## Changing something you already said
 *
 * The field is also where an edit is typed, and that is the decision rather
 * than an economy. The alternative — a message that becomes a text box in
 * place — was built in every chat product that has this, and in a panel 280px
 * wide it means the row you are editing is the only row you can see, the
 * emoji palette has nowhere to open, and the `@` list covers the
 * conversation. Everything this component already does is what editing needs.
 *
 * So editing is a mode of the composer, announced by the bar above the field
 * the way a reply is, and three things change while it is on. The button says
 * Save, because pressing something labelled Send to not send a message is a
 * button lying about what it does. Escape leaves the mode. And the draft that
 * was in the field is put back afterwards — the parent holds it, because the
 * parent already holds the draft and this component owns nothing that
 * survives its own unmount.
 *
 * A reply and an edit are never both on: starting one ends the other. They
 * compete for the same bar and the same field, and more to the point they are
 * contradictory intentions — the text in the box is either a new message
 * answering somebody or a replacement for an old one, and there is no frame on
 * the wire for both.
 */
export interface ComposerProps {
  value: string;
  onChange(text: string): void;
  /** True while the socket is open and welcomed. */
  ready: boolean;
  /** Who is in the room: what `@` offers, and how a name becomes a tag. */
  members: Member[];
  you: Member | null;
  /** The message being answered, or null. */
  replyTo: ChatRow | null;
  onCancelReply(): void;
  /** The message being rewritten, or null. Never both this and `replyTo`. */
  editing: ChatRow | null;
  onCancelEdit(): void;
  /** Returns true if the text was sent, in which case the draft is cleared. */
  onSend(text: string, opts?: { replyTo?: number; mentions?: string[] }): boolean;
  /** Returns true if the change went, in which case the field is given back
   *  to whatever was being written before. */
  onSave(text: string): boolean;
  /** The picture picked and not yet sent, and the two ways to change it. */
  attach: AttachmentApi;
  /**
   * Whether this deployment takes pictures at all.
   *
   * Hidden rather than disabled-with-a-reason, which is the opposite of what
   * this panel does for Pin. The difference is that a disabled Pin explains a
   * rule a learner can satisfy by signing in, and this would explain a
   * decision made by whoever runs the deployment, to somebody who can do
   * nothing about it — the same argument that hides Report on your own
   * message. Where the feature is off it is not off *for you*, it is not here.
   */
  pictures: boolean;
}

const WARN_AT = Math.floor(LIMITS.text * 0.8);
/** How many of the last-used emoji the palette keeps in front. */
const RECENT_MAX = 8;
/** How many names the `@` list offers. More than this is a directory, not a hint. */
const SUGGEST_MAX = 6;

export function Composer({
  value,
  onChange,
  ready,
  members,
  you,
  replyTo,
  onCancelReply,
  editing,
  onCancelEdit,
  onSend,
  onSave,
  attach,
  pictures,
}: ComposerProps) {
  const s = useStrings().chat;
  const ref = useRef<HTMLTextAreaElement>(null);
  const [palette, setPalette] = useState(false);
  const [recent, setRecent] = useState<readonly string[]>([]);
  /** Where the caret is, as far as the suggestion list is concerned. */
  const [caret, setCaret] = useState(0);
  /** Which suggestion the arrow keys are standing on. */
  const [pick, setPick] = useState(0);
  /** The `@` whose list was dismissed with Escape, so it stays dismissed. */
  const [dismissed, setDismissed] = useState<number | null>(null);
  /** The hidden file input the paperclip opens. */
  const fileRef = useRef<HTMLInputElement>(null);
  const length = value.length;
  const over = length > LIMITS.text;
  const empty = value.trim().length === 0;
  const picture = attach.attachment;
  /**
   * Whether there is anything to send.
   *
   * Not `empty` any more, because a screenshot with no caption is a message.
   * A picture still on its way up is not one yet and neither is one that
   * failed, so both of those keep the button off — which is the same test the
   * panel makes again on the way out, because a disabled attribute is a
   * suggestion and the state is the rule.
   */
  const nothing = empty && !(picture && picture.image);

  const typing = typedMention(value, caret);
  const suggestions =
    typing !== null && typing.at !== dismissed ? mentionCandidates(typing.query, members, you).slice(0, SUGGEST_MAX) : [];
  const suggesting = suggestions.length > 0;
  const chosen = suggesting ? suggestions[Math.min(pick, suggestions.length - 1)]! : null;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Collapse first so shrinking works; the stylesheet caps the height.
    // The box is border-box, so the border has to be added back on top of
    // scrollHeight or one line always overflows by two pixels and the box
    // grows a scrollbar with nothing to scroll.
    el.style.height = '0px';
    const border = el.offsetHeight - el.clientHeight;
    el.style.height = `${el.scrollHeight + border}px`;
  }, [value]);

  const submit = () => {
    if (!ready || nothing || over) return;

    // An edit replaces words and does nothing else. No `mentions` is worked
    // out and none is sent, because the room would refuse to act on them
    // anyway — an edit that could address somebody new would be the one way
    // to put a line in an arbitrary inbox without ever posting it there
    // (packages/chat, `edit`). Working them out here and having them dropped
    // on the wire would be this component promising a highlight the reader
    // will not get.
    if (editing) {
      if (onSave(value)) {
        setPalette(false);
        setDismissed(null);
      }
      return;
    }

    // Worked out from the finished text rather than remembered: what the
    // reader will see highlighted is what this sends.
    const mentions = mentionTags(value, members, you);
    const opts: { replyTo?: number; mentions?: string[] } = {};
    if (replyTo && replyTo.id > 0) opts.replyTo = replyTo.id;
    if (mentions.length > 0) opts.mentions = mentions;
    if (onSend(value, opts)) {
      onChange('');
      setPalette(false);
      setDismissed(null);
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit();
  };

  /** Where the caret is now, whatever moved it. */
  const syncCaret = (e: { currentTarget: HTMLTextAreaElement }) => setCaret(e.currentTarget.selectionStart ?? 0);

  /** Put the highlighted name in, and leave the caret after it. */
  const take = (member: Member) => {
    const el = ref.current;
    if (!typing || !el) return;
    // The whole of what has been typed since the `@`, replaced by the name it
    // was reaching for, and a space after it: the next word is a word, not a
    // continuation of the name.
    const next = `${value.slice(0, typing.at)}@${member.name} ${value.slice(caret)}`;
    const at = typing.at + member.name.length + 2;
    onChange(next);
    setDismissed(null);
    setPick(0);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(at, at);
      setCaret(at);
    });
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    if (suggesting && chosen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setPick((was) => (was + (e.key === 'ArrowDown' ? 1 : suggestions.length - 1) + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        // Enter takes the name rather than sending the line. Somebody halfway
        // through typing a name has not finished the sentence.
        e.preventDefault();
        take(chosen);
        return;
      }
      if (e.key === 'Escape') {
        // Ours, not the panel's: one Escape puts the list away and leaves the
        // draft alone, a second closes the chat.
        e.preventDefault();
        e.stopPropagation();
        setDismissed(typing?.at ?? null);
        return;
      }
    }
    // Ours before the panel's, and after the `@` list's above: one Escape
    // puts the suggestion list away, the next leaves the edit, and only then
    // does a third close the chat. Each of the three undoes the smallest
    // thing still open, which is the order a person expects and the reverse of
    // the order they were opened in.
    if (e.key === 'Escape' && editing) {
      e.preventDefault();
      e.stopPropagation();
      onCancelEdit();
      return;
    }
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
    e.preventDefault();
    submit();
  };

  const typed = (e: ReactChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value;
    // Expanded only when the caret is at the end, which is where it is for
    // anyone typing. Rewriting the value under a caret that sits in the
    // middle of a sentence would move it, and a cursor that jumps while you
    // are editing is worse than a `:shrug:` that stayed as words.
    const atEnd = e.target.selectionStart === next.length;
    setCaret(e.target.selectionStart ?? next.length);
    setPick(0);
    onChange(atEnd ? expandShortcodes(next) : next);
  };

  /** Put one in at the caret, and leave the caret after it. */
  const insert = (emoji: string) => {
    const el = ref.current;
    if (!el) {
      onChange(value + emoji);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? start;
    onChange(value.slice(0, start) + emoji + value.slice(end));
    setRecent((was) => [emoji, ...was.filter((c) => c !== emoji)].slice(0, RECENT_MAX));
    // After React has written the new value, or the caret lands where the
    // old string ended.
    requestAnimationFrame(() => {
      el.focus();
      const at = start + emoji.length;
      el.setSelectionRange(at, at);
      setCaret(at);
    });
  };

  return (
    <form className="chat-compose" onSubmit={onSubmit}>
      {/* That the field is holding an old message rather than a new one,
          in the same bar and the same place as a reply, because they are the
          same warning: what you are about to press is not what it usually
          does. An edit you have forgotten you are in is a typo pasted over a
          sentence somebody answered.

          No quote of the original beside it, unlike the reply bar. The
          original is in the field — that is the whole of what this mode is —
          and showing it twice would be the panel quoting the thing being
          typed. */}
      {/* The bands above the field, in one container.

          There can be two of them now — you can be answering somebody *and*
          holding a picture — and the grid underneath places every cell by
          hand, so a second full-width row would have meant a second `:has()`
          rule shifting everything down again, and a third for both at once.
          One wrapper that is the band area, holding however many bands there
          are, is the version of that which does not grow a rule per
          combination. */}
      {(editing || replyTo || picture) && (
      <div className="chat-compose-top">
      {editing && (
        <div className="chat-reply-bar is-edit">
          <span className="label">{s.composer.editing}</span>
          <button type="button" className="chat-x" aria-label={s.composer.cancelEdit} title={s.composer.cancelEdit} onClick={onCancelEdit}>
            <svg viewBox="0 0 12 12" aria-hidden="true" width="10" height="10">
              <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      )}

      {/* Who is being answered, and the way out of it. Above the field,
          because that is where it is looked at last before Send. */}
      {replyTo && !editing && (
        <div className="chat-reply-bar">
          <span className="label">
            <Parts
              of={s.reply.to(
                <span key="name" dir="auto">
                  {replyTo.name}
                </span>,
              )}
            />
          </span>
          <span className="chat-reply-text" dir="auto">
            <RichText text={snippetOf(replyTo.text)} />
          </span>
          <button type="button" className="chat-x" aria-label={s.reply.cancel} title={s.reply.cancel} onClick={onCancelReply}>
            <svg viewBox="0 0 12 12" aria-hidden="true" width="10" height="10">
              <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      )}

      {/* What is about to be sent with the words. Above the field with the
          reply and edit bars, because it is the same kind of thing: a state
          the composer is in that you would otherwise forget you were in.

          It stays on screen when the upload fails rather than vanishing with
          a toast. The learner is looking at the thing that went wrong, and
          taking it away is how somebody ends up dragging the same file in four
          times. The preview is a local object URL and never depended on the
          upload, so there is always something to show. */}
      {picture && (
        <div className={picture.failed ? 'chat-attach is-failed' : 'chat-attach'}>
          <img className="chat-attach-shot" src={picture.preview} alt="" />
          <span className="chat-attach-say">
            {picture.failed ? s.composer.refused(picture.failed) : picture.image ? s.composer.attached : s.composer.attaching}
          </span>
          <button
            type="button"
            className="chat-x"
            aria-label={s.composer.attachRemove}
            title={s.composer.attachRemove}
            onClick={attach.clear}
          >
            <svg viewBox="0 0 12 12" aria-hidden="true" width="10" height="10">
              <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      )}

      </div>
      )}

      {suggesting && (
        <div id="chat-mention-list" className="chat-menu chat-mention-list" role="listbox" aria-label={s.mention.label}>
          {suggestions.map((m, i) => (
            <div
              key={m.tag}
              id={`chat-mention-${m.tag}`}
              className={i === Math.min(pick, suggestions.length - 1) ? 'chat-menu-item is-on' : 'chat-menu-item'}
              role="option"
              aria-selected={i === Math.min(pick, suggestions.length - 1)}
              // Mouse down rather than click: a click would have moved focus
              // out of the field first, and the caret this inserts at would
              // already be gone.
              onMouseDown={(e) => {
                e.preventDefault();
                take(m);
              }}
            >
              <Identity m={m} />
            </div>
          ))}
        </div>
      )}

      {palette && (
        <EmojiPicker
          recent={recent}
          onPick={insert}
          onClose={() => {
            setPalette(false);
            ref.current?.focus();
          }}
        />
      )}

      <button
        type="button"
        className={palette ? 'compose-emoji is-on' : 'compose-emoji'}
        aria-expanded={palette}
        aria-label={s.composer.emoji}
        title={s.composer.emoji}
        onClick={() => setPalette((p) => !p)}
      >
        <span className="emoji" aria-hidden="true">
          🙂
        </span>
      </button>

      {/* The third way in, after the drop and the paste, and the only one that
          works on a phone or from a keyboard alone. The input itself is
          off-screen rather than `display: none`, because a hidden input is not
          focusable and the button is what carries the label — `.sr-only` is
          the class this panel already uses for exactly that.

          `capture` is deliberately not set: on a phone that would open the
          camera instead of the library, and the thing people send here is a
          screenshot far more often than a photograph. */}
      {pictures && (
        <>
      <input
        ref={fileRef}
        className="sr-only"
        type="file"
        accept="image/*"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) attach.pick(file);
          // Cleared so that picking the same file twice in a row fires a
          // second change event. Without this, removing a picture and
          // choosing it again does nothing at all.
          e.target.value = '';
        }}
      />
      <button
        type="button"
        className="compose-attach"
        aria-label={s.composer.attach}
        title={s.composer.attach}
        onClick={() => fileRef.current?.click()}
      >
        <svg viewBox="0 0 16 16" aria-hidden="true" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.6 5.1 6.2 9.5a1.6 1.6 0 0 0 2.3 2.3l4.6-4.6a3.1 3.1 0 0 0-4.4-4.4L4 7.5a4.6 4.6 0 0 0 6.5 6.5l3.9-3.9" />
        </svg>
      </button>
        </>
      )}

      <textarea
        ref={ref}
        className="chat-input"
        rows={1}
        value={value}
        placeholder={s.composer.message}
        aria-label={s.composer.message}
        autoComplete="off"
        autoFocus
        aria-autocomplete="list"
        aria-expanded={suggesting}
        aria-controls={suggesting ? 'chat-mention-list' : undefined}
        aria-activedescendant={chosen ? `chat-mention-${chosen.tag}` : undefined}
        // The box takes the direction of what is typed into it rather than the
        // direction of the page: a room about git is full of English pasted
        // into Arabic and the other way round, and a composer that fixed one
        // direction would put the caret on the wrong side for half of it.
        dir="auto"
        onChange={typed}
        onKeyDown={onKeyDown}
        // Every caret move, however it was made: the `@` list is a question
        // about where the caret is standing, not about what was last typed.
        // Three handlers rather than one, because `select` is not fired for a
        // collapsed caret in every browser — a click into the middle of a
        // line, or Home, would otherwise leave the list answering a question
        // about where the caret used to be.
        onSelect={syncCaret}
        onKeyUp={syncCaret}
        onClick={syncCaret}
        /**
         * A screenshot on the clipboard, which on Windows and most Linux
         * desktops is what PrintScreen leaves there — so this is the way most
         * people will actually attach one, ahead of the drop and well ahead of
         * the picker.
         *
         * The default is only cancelled when a picture is taken. A paste that
         * is text is left entirely alone, including the shortcode expansion it
         * already gets on the way through `typed`, and a paste that carries
         * both an image and text keeps the text: the clipboard's own idea of
         * "the same thing in several forms" is not something to second-guess
         * when one of the forms is what the person can see.
         */
        onPaste={(e) => {
          const file = pictures ? imageFrom(e.clipboardData) : null;
          if (!file) return;
          e.preventDefault();
          attach.pick(file);
        }}
      />
      <Button
        type="submit"
        variant="solid"
        disabled={!ready || nothing || over}
        title={editing ? s.composer.saveWhy : s.composer.sendWhy}
      >
        {editing ? s.composer.save : s.composer.send}
      </Button>
      {length > WARN_AT && (
        /* A measurement, not a sentence: `480/500` is set left to right in
           both languages, the way a clock is. */
        <span className={over ? 'chat-count is-over' : 'chat-count'} dir="ltr">
          {s.composer.count(length, LIMITS.text)}
        </span>
      )}
    </form>
  );
}

export default Composer;
